import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, siteInfo } from '@/mocks/articles';

const categoryNames: Record<string, string> = {
  'platform-recommend': '平台推荐',
  'bonus-guide': '领取攻略',
  'platform-review': '平台评测',
  'newbie-faq': '常见问题',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(
    () => article ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3) : [],
    [article]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | ${siteInfo.name}`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) metaDescription.setAttribute('content', article.excerpt);
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-1 pt-16 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                <i className="ri-error-warning-line text-2xl text-foreground-400 w-6 h-6 flex items-center justify-center"></i>
              </div>
              <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-sm text-foreground-500 mb-4">您要查看的文章可能已被移除或链接无效</p>
              <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">返回首页</Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      logo: {
        '@type': 'ImageObject',
        url: `${siteInfo.url}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.slug}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: categoryNames[article.category] || article.category, path: `/category/${article.category}` },
    { label: article.title },
  ];

  const renderContent = (content: string) => {
    const parts = content.split('\n\n');
    return parts.map((part, i) => {
      const trimmed = part.trim();
      if (trimmed.startsWith('## ')) {
        return <h2 key={i} id={`heading-${i}`}>{trimmed.replace('## ', '')}</h2>;
      }
      if (trimmed.startsWith('### ')) {
        return <h3 key={i}>{trimmed.replace('### ', '')}</h3>;
      }
      if (trimmed.startsWith('|')) {
        const rows = trimmed.split('\n').filter(r => r.startsWith('|'));
        if (rows.length > 0) {
          const headers = rows[0].split('|').filter(Boolean).map(h => h.trim());
          const dataRows = rows.slice(2).filter(r => r.includes('|'));
          return (
            <div key={i} className="overflow-x-auto">
              <table>
                <thead>
                  <tr>{headers.map((h, j) => <th key={j}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {dataRows.map((row, ri) => (
                    <tr key={ri}>{row.split('|').filter(Boolean).map((cell, ci) => <td key={ci}>{cell.trim()}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
      }
      if (trimmed.startsWith('Q') && trimmed.includes('A:')) {
        const [q, a] = trimmed.split('\nA:');
        return (
          <blockquote key={i}>
            <p className="font-semibold mb-1">{q.trim()}</p>
            <p>{a.trim()}</p>
          </blockquote>
        );
      }
      return <p key={i}>{trimmed}</p>;
    });
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <article>
            <div className="bg-background-100 border-b border-background-200/70">
              <div className="max-w-4xl mx-auto px-4 md:px-6">
                <Breadcrumb items={breadcrumbItems} />
                <div className="pb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-md whitespace-nowrap">
                      {categoryNames[article.category]}
                    </span>
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      {article.date}
                    </span>
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      {article.readTime}
                    </span>
                  </div>
                  <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3 leading-tight">
                    {article.title}
                  </h1>
                  <p className="text-sm md:text-base text-foreground-500 leading-relaxed">{article.excerpt}</p>
                </div>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1 max-w-4xl">
                  <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  <div className="article-content bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70">
                    {renderContent(article.content)}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-6">
                    {article.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-10 p-6 bg-primary-50 rounded-lg border border-primary-200/60 text-center">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">找到适合你的注册彩金平台了吗？</h3>
                    <p className="text-sm text-foreground-600 mb-4">浏览我们推荐的优质平台，立即领取你的专属新人彩金</p>
                    <Link
                      to="/category/platform-recommend"
                      className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                    >
                      查看平台推荐
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </div>

                <aside className="lg:w-80 flex-shrink-0">
                  <div className="sticky top-24 space-y-6">
                    <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">相关文章</h3>
                      {relatedArticles.length > 0 ? (
                        <div className="space-y-4">
                          {relatedArticles.map((ra) => (
                            <Link
                              key={ra.slug}
                              to={`/article/${ra.slug}`}
                              className="flex gap-3 group cursor-pointer"
                            >
                              <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                                <img src={ra.image} alt={ra.title} className="w-full h-full object-cover object-top" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                                  {ra.title}
                                </h4>
                                <span className="text-xs text-foreground-400 mt-1 block">{ra.date}</span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-foreground-400">暂无相关文章</p>
                      )}
                    </div>

                    <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">热门标签</h3>
                      <div className="flex flex-wrap gap-2">
                        {Array.from(new Set(articles.flatMap((a) => a.tags))).slice(0, 8).map((tag) => (
                          <span key={tag} className="px-3 py-1 bg-background-50 border border-background-200/70 text-foreground-600 text-xs rounded-full whitespace-nowrap cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}